/**
 * Reload once when a new service worker takes over this page - §9.357, FB-640.
 *
 * **The owner's report, on the first day `pizzamajstro.sk` served AGOS**: *"when I was already
 * opened in pizzamajstro, from old system - the cache is broken... sometimes I see blank page with
 * only title called 'Uvodna stranka' nothing else"*, and a login that answered *"No static resource
 * api/customer-auth/login"*.
 *
 * **Both are legacy's bundle still running on this origin.** Legacy builds a service worker
 * (`vite-plugin-pwa`, `registerType: autoUpdate`), so every returning customer has one registered,
 * and a service worker intercepts fetches **before** HTTP caching - which is why `no-cache` on
 * `index.html` does not help. The document comes from the worker's precache, naming a bundle AGOS no
 * longer has, so the page renders nothing but its `<title>`. When the bundle *does* come from that
 * cache, it calls legacy's unversioned API paths, which AGOS does not map.
 *
 * **Why not the manager's fix.** `apps/manager/src/evict-service-worker.ts` unregisters every worker
 * on its origin, which is right there: the manager is not a PWA and has never had one, so anything
 * present is foreign. Here the current worker is **ours** - it receives push, and
 * `customer_push_subscriptions` has 114 rows pointing at it. Unregistering would break that.
 *
 * **What was actually missing.** The worker in `service-worker.ts` already calls `skipWaiting()` on
 * install and `clients.claim()` on activate, so it seizes control within one update check. But
 * `clients.claim()` **does not reload the document** - the tab in front of the customer is still the
 * stale one the old worker served, so the fix appears not to work on the very visit that performs
 * it. `controllerchange` fires exactly when the new worker takes the page over, and that is the
 * moment a reload is both safe and necessary.
 */

/** Marks the reload as done for this tab, so a worker that keeps changing cannot loop. */
const RELOADED = 'agos.sw-takeover-reloaded'

/**
 * Whether a reload should happen now - separated from performing it so it can be tested.
 *
 * @param hadController whether a worker was already controlling the page when it loaded. **A first
 *     visit has none**, and the worker that claims a page it precached is not replacing anything -
 *     reloading there would be a gratuitous double load on every new customer.
 */
export function shouldReloadOnTakeover(hadController: boolean): boolean {
  if (!hadController) return false
  try {
    if (sessionStorage.getItem(RELOADED) === '1') return false
    sessionStorage.setItem(RELOADED, '1')
  } catch {
    // Private modes can refuse `sessionStorage`. Without the mark a reload could repeat, and §9.282
    // settled which way to fail: a reload loop is worse than a stale worker. The new worker has
    // claimed the page regardless, so the next navigation is clean either way.
    return false
  }
  return true
}

/**
 * Listen for the takeover. Safe to call unconditionally, on every load.
 *
 * <p>Absent on insecure origins and in some embedded browsers, and a customer app that failed to
 * start because it could not arrange a reload would be a far worse bug than the one this fixes.
 */
export function reloadOnWorkerTakeover(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  // Read **now**, not inside the handler: by the time `controllerchange` fires the controller is the
  // new one, so asking then would always say yes and every first visit would reload.
  const hadController = navigator.serviceWorker.controller !== null

  try {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (shouldReloadOnTakeover(hadController)) location.reload()
    })
  } catch {
    // A browser that refuses the listener is one this cannot help, and there is nothing a customer
    // could do with the error.
  }
}
